import { getCityPlanes } from '../api/client';

/**
 * Texto SEO específico por ciudad (planes en /ciudades/:slug).
 * Cada entrada: lugares, ideas de planes y tono local.
 */

export interface CityPlanesSeoContent {
  title: string;
  paragraphs: [string, string];
}

const DEFAULT_TITLE = (name: string) => `Planes entre mujeres en ${name}`;

const FALLBACK = (displayName: string): CityPlanesSeoContent => ({
  title: DEFAULT_TITLE(displayName),
  paragraphs: [
    `En ${displayName}, Chisme conecta a mujeres que buscan planes reales fuera del móvil: cafés entre barrios, cultura, deporte suave o una tarde tranquila en grupo. Los encuentros surgen cuando alguien propone una idea en la app y el resto se apunta, con sitio y hora claros.`,
    `Explora los planes publicados aquí, filtra por día y descarga Chisme para unirte o proponer el siguiente plan en ${displayName}. Cuantas más personas participan, más opciones hay para conocer gente y descubrir la ciudad.`,
  ],
});

const BY_SLUG: Record<string, CityPlanesSeoContent> = {
  barcelona: {
    title: 'Planes en Barcelona: barrios, playa y quedadas con Chisme',
    paragraphs: [
      'Barcelona mezcla barrios con mucha vida —Gràcia, el Eixample, el Born o la Barceloneta— con rincones para café, brunch o una vuelta antes de la playa. En Chisme verás propuestas como yoga al aire libre, cenas informales o tardes de cerámica; los planes nacen cuando una usuaria lanza la idea y otras se apuntan con tiempo y lugar concretos.',
      'Si quieres descubrir terrazas, rutas por el paseo marítimo o quedadas cerca del centro, filtra por día en esta página y únete desde la app. Proponer un plan en Barcelona es ideal para conocer mujeres que comparten barrio o afición, sin perderse en grupos enormes.',
    ],
  },
  madrid: {
    title: 'Planes en Madrid: Malasaña, Retiro y terrazas con Chisme',
    paragraphs: [
      'Madrid ofrece desde el pulso de Malasaña y Chueca hasta el respiro del Retiro o las terrazas del centro. Con Chisme puedes unirte a desayunos entre semana, rutas culturales o tardes de café cerca de Gran Vía; cada plan lo organiza alguien de la comunidad y se rellena con quienes realmente pueden venir.',
      'Explora los planes por fecha, elige un barrio que te encaje y descarga la app para crear el siguiente encuentro: museos, paseos o una cena ligera. Es una forma sencilla de ampliar tu círculo en la capital sin agendar a ciegas.',
    ],
  },
  valencia: {
    title: 'Planes en Valencia: Turia, playa y barrio del Carmen',
    paragraphs: [
      'Valencia combina el jardín del Turia, la Ciudad de las Artes y las Ciencias y el casco del Carmen con la playa a un salto. Los planes en Chisme suelen girar en torno a brunch, bicis por el cauce, tardes de café o actividades suaves al aire libre; todo queda claro en la app antes de quedar.',
      'Filtra por día, mira qué hay cerca de tu zona y apúntate o propón un plan: desde una vuelta por el mercado hasta una tarde en la Malvarrosa. Así conoces mujeres de la ciudad con gustos parecidos y sin complicaciones.',
    ],
  },
  sevilla: {
    title: 'Planes en Sevilla: Triana, tapas y encuentros con Chisme',
    paragraphs: [
      'Sevilla invita a pasear por Triana, el casco junto a la Giralda o a sentarse a tapas en terrazas con sombra. En Chisme encontrarás quedadas para café, cultura flamenca a pequeña escala o planes tranquilos al anochecer; cada propuesta tiene anfitriona y cupo para que el grupo sea manejable.',
      'Usa esta página para ver qué hay por fechas y barrios, y entra en la app para unirte o lanzar tu plan: ideal si quieres compartir rutina en una ciudad donde el callejear es un plan en sí mismo.',
    ],
  },
  zaragoza: {
    title: 'Planes en Zaragoza: Pilar, Ebro y quedadas con Chisme',
    paragraphs: [
      'Zaragoza se vive entre el Pilar, el casco histórico y el paseo por el Ebro. Con Chisme salen planes como cafés cerca del centro, rutas culturales o tardes tranquilas en grupo reducido; la app evita el ruido de grupos gigantes y deja claro cuándo y dónde os veis.',
      'Revisa el calendario de planes en esta página y descarga Chisme para proponer una quedada o unirte a la siguiente: buena opción si buscas conocer mujeres locales con ritmo pausado y sitios concretos.',
    ],
  },
  malaga: {
    title: 'Planes en Málaga: costa, casco antiguo y planes con Chisme',
    paragraphs: [
      'Málaga une playa en la Malagueta, casco antiguo lleno de terrazas y cultura (museo Picasso, teatro). Los planes en Chisme incluyen brunch con vistas, paseos por el puerto o actividades suaves entre semana; cada una elige el tono y el barrio para que encaje con quien se apunta.',
      'Filtra por día, mira qué hay cerca de ti y únete desde la app o crea un plan nuevo: perfecto para descubrir rincones de la costa y conocer mujeres que también quieren salir del chat.',
    ],
  },
  bilbao: {
    title: 'Planes en Bilbao: Casco Viejo, ría y pintxos con Chisme',
    paragraphs: [
      'Bilbao se disfruta entre el Casco Viejo, la ría y el entorno del Guggenheim. En Chisme verás propuestas de pintxos tranquilos, café entre semana o paseos cortos en grupo pequeño; la anfitriona marca el sitio y la hora para que nadie vaya a ciegas.',
      'Explora los planes publicados aquí y entra en la app para unirte o lanzar el siguiente encuentro: una forma directa de ampliar tu círculo en una ciudad donde el plan suele ser “un sitio y buena compañía”.',
    ],
  },
  miami: {
    title: 'Planes en Miami: South Beach, Wynwood y quedadas con Chisme',
    paragraphs: [
      'Miami se vive entre South Beach, Brickell, Wynwood y Little Havana: brunch con palmeras, arte en las calles, café cubano o un paseo al atardecer. En Chisme verás propuestas como yoga en la playa, cenas informales o tardes de terraza; cada plan lo lanza alguien de la comunidad con sitio y hora claros.',
      'Filtra por día, elige el barrio que te encaje y únete desde la app o crea el siguiente encuentro: ideal si quieres conocer mujeres en Miami con ganas de quedar de verdad, sin perderse en grupos enormes.',
    ],
  },
  orlando: {
    title: 'Planes en Orlando: Lake Eola, Winter Park y quedadas con Chisme',
    paragraphs: [
      'Orlando es más que parques: Lake Eola, Thornton Park, Winter Park y el centro se prestan a café, brunch, paseos o una tarde tranquila en grupo. En Chisme los planes nacen cuando alguien propone la idea —yoga al aire libre, cenas informales o una vuelta por el lago— con sitio y hora claros.',
      'Filtra por día, mira qué hay cerca de ti y únete desde la app o lanza el siguiente encuentro: una forma sencilla de conocer mujeres en Orlando que también quieren quedar de verdad, más allá del turismo.',
    ],
  },
  tampa: {
    title: 'Planes en Tampa: Hyde Park, Ybor y el Riverwalk con Chisme',
    paragraphs: [
      'Tampa se disfruta entre Hyde Park, Ybor City, el Riverwalk y Bayshore: café, arte, un paseo junto al agua o una cena sin prisa. En Chisme verás propuestas de brunch, rutas suaves o tardes en terraza; cada plan lo organiza alguien de la comunidad y se rellena con quienes realmente pueden venir.',
      'Explora los planes por fecha, elige el barrio que te encaje y descarga la app para unirte o crear el siguiente: ideal si buscas compañía real en Tampa, con grupos manejables y sitios concretos.',
    ],
  },
  palma: {
    title: 'Planes en Palma: casco, paseo marítimo y calas con Chisme',
    paragraphs: [
      'Palma de Mallorca combina casco antiguo, paseo marítimo y calas a pocos minutos. Los planes en Chisme suelen ser cafés con vistas, rutas suaves o tardes sin prisa en grupo reducido; ideal si buscas compañía para descubrir la ciudad más allá de la temporada turística.',
      'Consulta el listado por fechas, elige lo que encaje con tu barrio y descarga la app para proponer o apuntarte al siguiente plan: así conectas con mujeres que viven la isla al mismo ritmo que tú.',
    ],
  },
};

export function getCityPlanesSeoContent(
  slug: string | undefined,
  displayName: string
): CityPlanesSeoContent {
  const key = (slug ?? '').toLowerCase();
  return BY_SLUG[key] ?? FALLBACK(displayName);
}

export async function getCityPlanesSeoContentFromApi(
  slug: string | undefined,
  displayName: string
): Promise<CityPlanesSeoContent> {
  const citySlug = (slug ?? '').toLowerCase();
  if (!citySlug) {
    return FALLBACK(displayName);
  }

  try {
    const { seo } = await getCityPlanes(citySlug);
    return seo;
  } catch {
    return getCityPlanesSeoContent(slug, displayName);
  }
}
